import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonAccordionGroup, ModalController, NavController } from '@ionic/angular';

import { OnboardingProgress, SlackChannel } from 'src/models';
import { Project } from 'src/models/projects';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SlackService } from 'src/services/slack.service';

import { AddMemberPage } from '../components/add-member/add-member.page';
import { DEFAULT_SUPPORT_STEPS, INTEGRATION_TYPES } from '../constants';

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
})
export class OnboardingComponent implements OnInit {
  @ViewChild('accordionGroup', { static: false }) accordionGroup: IonAccordionGroup;
  @ViewChild('advancedAccordion', { static: false }) advancedAccordion: IonAccordionGroup;
  @ViewChild('aiAccordion', { static: false }) aiAccordion: IonAccordionGroup;

  defaultTriageChannelId: string;
  defaultSupportSteps: any[] = [];
  triageChannel?: SlackChannel;
  defaultProject: Partial<Project>;

  onboardingProgress: OnboardingProgress;
  isLoading = false;
  isDone = false;
  isInternalSupport: boolean;

  aiOnboardingSteps = [
    {
      title: 'Add Your Knowledge Base to Train the AI',
      description:
        'Connect your public docs or private wikis, leveraging web scraping as well as native integrations with Confluence, Google Drive, Notion, and more.',
      key: 'isArticleAdded',
      buttonTitle: 'Add Articles',
      buttonPath: '/dashboard/knowledge',
      docs: 'https://docs.unthread.io/ai/knowledge-base',
    },
    {
      title: 'Enable AI Tagging',
      description: 'Create a set of tags to start capturing and analyzing the most common types of requests.',
      key: 'isTagAdded',
      buttonTitle: 'Manage Tags',
      buttonPath: '/dashboard/tags',
      docs: 'https://docs.unthread.io/ai/ai-tags',
    },
    {
      title: 'Build Your First Automation',
      description:
        'Automations are a powerful way to customize your workflow. You can connect different apps or even write your own code to extend any built-in functionality.',
      key: 'isAutomationAdded',
      buttonTitle: 'Go to Automations',
      buttonPath: '/dashboard/automations',
      docs: 'https://docs.unthread.io/workflow/automations',
    },
  ];

  constructor(
    private route: ActivatedRoute,
    public authService: AuthService,
    private slackService: SlackService,
    private navController: NavController,
    private apiService: ApiService,
    private modalCtrl: ModalController,
    private analyticsService: AnalyticsService,
    private msgService: MessageService,
    private projectsService: ProjectsService,
  ) {}

  async ngOnInit() {
    const tenant = this.authService.tenant;

    this.isInternalSupport = tenant.isInternalSupport;

    this.defaultProject = await this.projectsService.getById(tenant.defaultProjectId);

    this.defaultTriageChannelId = tenant?.defaultTriageChannelId ?? null;
    this.defaultSupportSteps = this.defaultProject?.supportSteps?.length
      ? this.defaultProject?.supportSteps
      : DEFAULT_SUPPORT_STEPS;

    this.onboardingProgress = {
      isSlackConnected: this.authService.integrations?.slackEnabled,
      isCustomerAdded: false,
      isAssignmentRuleSet: false,
      isTriageChannelSet: false,
      isAdminAdded: false,
      isEmailConnected: false,
      isWidgetConnected: false,
      isIntegrationAdded: false,
      isTaskManagerConnected: false,
      isCRMConnected: false,
      isTagAdded: false,
      isArticleAdded: false,
      isAutomationAdded: false,
    };

    if (this.defaultTriageChannelId) {
      this.loadTriageChannel();
    }

    if (this.route.snapshot.queryParamMap.get('autoinstall')) {
      this.installSlack();
    }

    this.getOnboardingProgress();
  }

  async loadTriageChannel() {
    const triageChannel = await this.slackService.getChannel(this.authService.tenant.defaultTriageChannelId);

    if (triageChannel) {
      this.triageChannel = triageChannel;
    }
  }

  onTriageChannelChange(channel?: SlackChannel) {
    this.defaultTriageChannelId = channel?.id ?? null;
    this.triageChannel = channel;

    this.saveChanges(false);
  }

  async ionViewWillEnter() {
    this.getOnboardingProgress();
  }

  async getOnboardingProgress() {
    try {
      const progress: OnboardingProgress = await this.apiService.getPromise('/onboarding-progress');
      this.onboardingProgress = {
        ...progress,
      };
      this.checkIfDone();
    } catch (e) {}
  }

  installSlack() {
    this.analyticsService.trackEvent('Onboarding', 'Install Slack');

    const redirect = `${window.location.origin}/customers/new`;
    this.slackService.oauth(redirect, window.location.origin);
  }

  playVideo() {
    this.analyticsService.trackEvent('Onboarding', `Play Onboarding Video`);

    window.open('https://www.loom.com/share/025b539ef4dd46eca3d78ed0c6ac46c4', '_blank');
  }

  addCustomers() {
    this.analyticsService.trackEvent('Onboarding', 'Add Customers');

    this.navController.navigateForward(`customers/new`);
  }

  async inviteTeamMember(isAdmin: boolean) {
    this.analyticsService.trackEvent('Onboarding', 'Add Team Members');

    const modal = await this.modalCtrl.create({
      component: AddMemberPage,
      cssClass: 'model-sm',
      componentProps: {
        isAdmin,
        welcomeEmailEnabled: true,
      },
    });

    modal.present();

    const { data } = await modal.onWillDismiss();

    if (data && data.email) {
      this.accordionGroup.value = undefined;
      this.getOnboardingProgress();
    }
  }

  setupTaskManager() {
    this.analyticsService.trackEvent('Onboarding', 'Set Up Task Manager');

    this.navController.navigateRoot(`dashboard/settings/apps?type=${INTEGRATION_TYPES.TASK_MANAGER}`);
  }

  setupCRM() {
    this.analyticsService.trackEvent('Onboarding', 'Set Up CRM');

    this.navController.navigateRoot(`dashboard/settings/apps?type=${INTEGRATION_TYPES.CRM}`);
  }

  setupEmail() {
    this.analyticsService.trackEvent('Onboarding', 'Set Up Email');

    this.navController.navigateRoot(`dashboard/settings/email`);
  }

  setupWidget() {
    this.analyticsService.trackEvent('Onboarding', 'Set Up In-App Chat');

    this.navController.navigateRoot(`dashboard/settings/in-app-chat`);
  }

  setupIntegrations() {
    this.analyticsService.trackEvent('Onboarding', 'Set Up Integrations');

    this.navController.navigateRoot(`dashboard/settings/service-accounts`);
  }

  defaultSupportStepsDidChange(defaultSupportSteps: any[]) {
    this.defaultSupportSteps = defaultSupportSteps;
  }

  async saveSkippedStep(stepType: string) {
    this.analyticsService.trackEvent('Onboarding', `Skip Step ${stepType}`);

    try {
      await this.authService.updateTenant({
        skippedOnboardingSteps: {
          [stepType]: true,
        },
      });
      this.msgService.showBottom('✔ Skipped this step. You can always add it later.');

      this.onboardingProgress[stepType] = true;

      // Automatically select the next accordion
      if (stepType === 'isAdminAdded') {
        this.accordionGroup.value = undefined;
        this.aiAccordion.value = this.aiOnboardingSteps[0].key;
      } else if (stepType === 'isCRMConnected') {
        this.advancedAccordion.value = 'email';
      } else if (stepType === 'isEmailConnected') {
        this.advancedAccordion.value = 'widget';
      } else if (stepType === 'isWidgetConnected') {
        this.advancedAccordion.value = 'integrations';
      } else if (stepType === 'isIntegrationAdded') {
        this.advancedAccordion.value = undefined;
      }
      this.checkIfDone();
    } catch (err) {
      this.isLoading = false;
      this.msgService.showBottom(`Whoops! Unable to skip this step: ${err.message}`);
    }
  }

  async saveChanges(isAssignmentRule: boolean) {
    const updates: any = {};

    if (this.defaultTriageChannelId) {
      updates.defaultTriageChannelId = this.defaultTriageChannelId;
    }

    if (this.defaultSupportSteps) {
      updates.defaultSupportSteps = this.defaultSupportSteps;
    }

    if (Object.keys(updates).length) {
      try {
        this.isLoading = true;
        await this.authService.updateTenant(updates);
        this.isLoading = false;

        this.getOnboardingProgress();

        const nativeEl = this.accordionGroup;

        // Automatically select the next accordion
        if (isAssignmentRule) {
          nativeEl.value = 'triage';
        } else {
          nativeEl.value = 'team';
        }
      } catch (err) {
        this.isLoading = false;
        this.msgService.showBottom(`Whoops! An error occurred: ${err.message}`);
      }
    }
  }

  checkIfDone() {
    this.isDone = false;
    if (!this.onboardingProgress.isSlackConnected) {
      this.accordionGroup.value = 'slack';
    } else if (!this.onboardingProgress.isCustomerAdded) {
      this.accordionGroup.value = 'customer';
    } else if (!this.onboardingProgress.isTriageChannelSet) {
      this.accordionGroup.value = 'triage';
    } else if (!this.onboardingProgress.isAssignmentRuleSet) {
      this.accordionGroup.value = 'assignment';
    } else if (!this.onboardingProgress.isAdminAdded) {
      this.accordionGroup.value = 'team';
    } else if (!this.onboardingProgress.isTaskManagerConnected) {
      this.advancedAccordion.value = 'tasks';
    } else if (!this.onboardingProgress.isCRMConnected) {
      this.advancedAccordion.value = 'crm';
    } else if (!this.onboardingProgress.isEmailConnected) {
      this.advancedAccordion.value = 'email';
    } else if (!this.onboardingProgress.isWidgetConnected) {
      this.advancedAccordion.value = 'widget';
    } else if (!this.onboardingProgress.isIntegrationAdded) {
      this.advancedAccordion.value = 'integrations';
    } else {
      this.isDone = true;
    }

    if (this.isDone) {
      this.accordionGroup.value = undefined;
      this.advancedAccordion.value = undefined;
    }
  }
}
